/**
 * Entities explicitly queued for human review. Unlike low-confidence results,
 * this is an unbounded work queue and is drained page-by-page.
 */

import { latestEvidence, latestManualEvidence } from '@atbash-labs/ontology-core'

import { asString, mapCypherNodeIdentity } from './cypher-row'
import { parseProvenance } from './flagged-nodes'

export const REVIEW_QUEUE_NODES_CYPHER = `
MATCH (n)
WHERE n.needs_review = true
RETURN n.id AS id, elementId(n) AS element_id, n.name AS name, labels(n) AS labels,
       n.confidence AS confidence, n.review_note AS review_note,
       n.tag_path AS tag_path, n.folder_name AS folder_name,
       n.provenance AS provenance
ORDER BY toLower(coalesce(n.name, n.id, ''))
SKIP toInteger($skip) LIMIT toInteger($pageSize)
`.trim()

export const REVIEW_QUEUE_RELATIONSHIPS_CYPHER = `
MATCH (a)-[r]->(b)
WHERE r.needs_review = true
RETURN elementId(r) AS element_id, type(r) AS type, r.confidence AS confidence,
       r.review_note AS review_note,
       coalesce(a.id, elementId(a)) AS source_id, coalesce(b.id, elementId(b)) AS target_id,
       coalesce(a.name, a.id) AS source_name, coalesce(b.name, b.id) AS target_name,
       r.provenance AS provenance
ORDER BY type(r), toLower(coalesce(a.name, a.id, ''))
SKIP toInteger($skip) LIMIT toInteger($pageSize)
`.trim()

interface ReviewReason {
  reason?: string
  flagged_by?: string
  flagged_at?: string
}

export interface ReviewQueueNode extends ReviewReason {
  id: string
  name?: string
  labels: string[]
  confidence?: number
  path?: string
}

export interface ReviewQueueRelationship extends ReviewReason {
  id: string
  type: string
  confidence?: number
  sourceId?: string
  targetId?: string
  sourceName?: string
  targetName?: string
}

function asConfidence(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

/**
 * Queue reason is the denormalized `review_note` only. Evidence attribution
 * (who/when) is still useful when present, but excerpts are not the reason.
 */
function mapReviewReason(row: Record<string, unknown>): ReviewReason {
  const reason = asString(row.review_note)
  const provenance = parseProvenance(row.provenance)
  const evidence =
    provenance === undefined
      ? undefined
      : (latestManualEvidence(provenance) ?? latestEvidence(provenance))
  const flaggedBy = asString(evidence?.created_by)
  const flaggedAt = asString(evidence?.created_at)

  return {
    ...(reason !== undefined ? { reason } : {}),
    ...(flaggedBy !== undefined ? { flagged_by: flaggedBy } : {}),
    ...(flaggedAt !== undefined ? { flagged_at: flaggedAt } : {})
  }
}

export function mapReviewQueueNodeRow(row: Record<string, unknown>): ReviewQueueNode | null {
  const identity = mapCypherNodeIdentity(row)
  if (identity === null) return null
  const confidence = asConfidence(row.confidence)
  return {
    id: identity.id,
    labels: identity.labels,
    ...(identity.name !== undefined ? { name: identity.name } : {}),
    ...(identity.path !== undefined ? { path: identity.path } : {}),
    ...(confidence !== undefined ? { confidence } : {}),
    ...mapReviewReason(row)
  }
}

export function mapReviewQueueRelationshipRow(
  row: Record<string, unknown>
): ReviewQueueRelationship | null {
  const id = asString(row.element_id)
  const type = asString(row.type)
  if (id === undefined || type === undefined) return null

  const confidence = asConfidence(row.confidence)
  const sourceId = asString(row.source_id)
  const targetId = asString(row.target_id)
  const sourceName = asString(row.source_name)
  const targetName = asString(row.target_name)
  return {
    id,
    type,
    ...(confidence !== undefined ? { confidence } : {}),
    ...(sourceId !== undefined ? { sourceId } : {}),
    ...(targetId !== undefined ? { targetId } : {}),
    ...(sourceName !== undefined ? { sourceName } : {}),
    ...(targetName !== undefined ? { targetName } : {}),
    ...mapReviewReason(row)
  }
}
